import { provide } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { getShowOrder, OrderDetailDto } from '@/api-sdk';
import { iconShoppingCart } from '@/components/icons';
import { global, GlobalComponentType } from '@/context';
import { BREAKPOINT_LARGE_PX, BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, formStyles, rootVariablesStyles } from '@/styles';
import { LayoutType, TotalItem } from '@/types';
import {
  formatDate,
  i18nInit,
  isDateValid,
  mapAddressDetails,
  setBaseUrl,
} from '@/utils';

import '../../loader/fg-loader';
import './fg-order-history-item';

@customElement('fg-order-history-details')
@localized()
export class FgOrderHistoryDetails extends LitElement {
  @property({ type: String })
  locale = '';

  @property({ type: String })
  debtor = '';

  @property({ type: String })
  baseUrl = '';

  @property({ type: String })
  salesOrderNumber = '';

  @property({ type: String })
  storeCode?: string;

  @property({ type: String })
  layout: LayoutType = 'wholesale';

  @state()
  private showConfigurationDetails = false;

  @provide({ context: global.context })
  globalContext!: GlobalComponentType;

  @state()
  orderLines: {
    shippingAddress: string[];
    invoiceAddress: string[];
    totals: TotalItem[];
  } = {
    shippingAddress: [],
    invoiceAddress: [],
    totals: [],
  };

  @state()
  private orderDetails: OrderDetailDto | null = null;

  @state()
  private isLoading = true;

  @state()
  private reorderDisabled = false;

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  async firstUpdated() {
    try {
      this.globalContext = {
        layout: this.layout,
        locale: this.locale,
        baseUrl: this.baseUrl,
        debtor: this.debtor,
      };
      const orderDetails = await getShowOrder({
        salesOrderNumber: this.salesOrderNumber,
        params: {
          locale: this.locale,
        },
      });

      this.orderDetails = orderDetails;
      this.orderLines.shippingAddress = mapAddressDetails(
        orderDetails?.shippingInfo?.address,
      );
      this.orderLines.invoiceAddress = mapAddressDetails(
        orderDetails?.billingInfo?.address,
      );
      this.orderLines.totals = this.mapTotals(orderDetails);
      if (orderDetails?.items?.length === 1) {
        this.showConfigurationDetails = true;
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  mapTotals(orderDetails: OrderDetailDto): TotalItem[] {
    const { totals, promotion } = orderDetails;
    const totalItems: TotalItem[] = [
      ...(promotion?.amount
        ? [
            {
              label: 'content.discount',
              value: promotion?.amount?.formatted
                ? `-${promotion?.amount?.formatted}`
                : '-',
              isDiscount: true,
            },
          ]
        : []),
      {
        label: 'labels.totalPriceExclVat',
        value: totals?.itemsExcludingVat?.formatted ?? '-',
        isTotalExclVat: true,
      },
      {
        label: 'labels.summaryShippingCosts',
        value: totals?.shippingCosts?.formatted ?? '-',
        isFree: totals?.shippingCosts?.value === '0.000',
      },
    ];

    if (this.globalContext.layout === 'retail') {
      totalItems.push({
        label: 'labels.totalPriceInclVat',
        value: totals?.includingVat?.formatted ?? '-',
      });
    }

    if (totals?.vat && totals?.vat?.formatted) {
      totalItems.push({
        label: 'labels.summaryVatPercentage',
        value: totals?.vat?.formatted ?? '-',
      });
    }

    return totalItems;
  }

  renderAddressBlock(title: string, addressItems: string[]) {
    return html`
      <div class="items-container">
        <h4 class="item-title">${t(title)}</h4>
        ${addressItems?.map(item => {
          return html`
            <p class="item-text">${item}</p>
          `;
        })}
      </div>
    `;
  }

  renderTotalsBlock() {
    return html`
      <div class="items-container">
        <h4 class="item-title">${t('labels.summaryTotalsTitle')}</h4>
        ${this.orderLines?.totals?.map(item => {
          return html`
            <div class="total-item-container">
              <span
                class="item-text ${classMap({
                  'total-excl-vat': Boolean(item?.isTotalExclVat),
                  discount: Boolean(item?.isDiscount),
                })}"
              >
                ${t(item.label)}:
              </span>
              <span
                class="item-text ${classMap({
                  'total-excl-vat total-excl-vat-value': Boolean(
                    item?.isTotalExclVat,
                  ),
                  free: Boolean(item?.isFree),
                  discount: Boolean(item?.isDiscount),
                })}"
              >
                ${item?.isFree ? t('content.configuration.free') : item.value}
              </span>
            </div>
          `;
        })}
      </div>
    `;
  }

  onReorder() {
    this.reorderDisabled = true;

    const event = new CustomEvent('reorder', { composed: true });
    this.dispatchEvent(event);
  }

  renderItemsContainer() {
    return this.orderDetails?.items?.map(item => {
      return html`
        <fg-order-history-item
          part=${this.layout}
          .orderItem=${item}
          .locale=${this.locale}
          .showConfigurationDetails=${this.showConfigurationDetails}
          .storeCode=${this.storeCode}
        ></fg-order-history-item>
      `;
    });
  }

  render() {
    if (this.isLoading) {
      return html`
        <fg-loader type="configuration"></fg-loader>
      `;
    }

    if (!this.orderDetails) {
      return '';
    }

    return html`
      <div class="container">
        <div class='${classMap({
          'retail-order-summary-container': this.layout === 'retail',
          'wholesale-order-summary-container': this.layout === 'wholesale',
        })} order-summary-container'>
          <div class="order-summary-content">
          <div>
            <h3 class="order-title">
              ${t('labels.order')}
              ${
                this.orderDetails?.salesOrderNumber
                  ? `#${this.orderDetails?.salesOrderNumber}`
                  : nothing
              }
            </h3>
            <div class="order-subtitle">
              ${
                isDateValid(this.orderDetails?.orderDate)
                  ? html`
                      <span>
                        ${formatDate(
                          this.orderDetails?.orderDate ?? '',
                          this.locale,
                          'short',
                        )}
                      </span>
                      |
                    `
                  : nothing
              }
              <span
                >${t('labels.summaryItems', {
                  count: this?.orderDetails?.items?.length ?? 0,
                })}</span
              >
            </div>
          </div>
              ${
                this.layout === 'wholesale'
                  ? html`
                      <button
                        class="reorder-button"
                        @click=${this.onReorder}
                        ?disabled=${this.reorderDisabled}
                      >
                        ${this.reorderDisabled
                          ? html`
                              <fg-loader
                                type="small"
                                .bgcolor=${'transparent'}
                              ></fg-loader>
                            `
                          : iconShoppingCart}
                        <span class="reorder-button-label">
                          ${this.reorderDisabled
                            ? t('labels.loading')
                            : t('labels.reorder')}
                        </span>
                      </button>
                    `
                  : nothing
              }
            </div>
            <div class="shipping-and-totals-container">
              <div class="addresses-container">
                ${this.renderAddressBlock(
                  'labels.shippingAddressTitle',
                  this.orderLines?.shippingAddress,
                )}
                ${this.renderAddressBlock(
                  'labels.invoiceAddressTitle',
                  this.orderLines?.invoiceAddress,
                )}
              </div>
              ${this.renderTotalsBlock()}
            </div>
          </div>
        </div>
        ${this.renderItemsContainer()}
      </div>
    `;
  }
  static styles = [
    baseStyles,
    formStyles,
    rootVariablesStyles,
    css`
      .container {
        width: 100%;
      }

      .free,
      .discount {
        color: var(--fg-success, var(--fg-fallback-success));
      }

      .addresses-container {
        display: flex;
        flex: 2;
        flex-direction: row;
        justify-content: space-between;
        padding-bottom: 12px;

        @media screen and (max-width: ${BREAKPOINT_LARGE_PX}px) {
          && {
            flex: 1;
          }
        }

        @media screen and (max-width: ${BREAKPOINT_SMALL_PX}px) {
          && {
            flex-direction: column;
            flex: 1;
          }
        }
      }

      .retail-order-summary-container {
        background-color: #ffffff;
      }

      .wholesale-order-summary-container {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
      }

      .order-summary-container {
        border-radius: var(--fg-borderradius);
        padding: 12px;
      }

      .order-summary-content {
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        @media screen and (max-width: ${BREAKPOINT_SMALL_PX}px) {
          && {
            flex-direction: column;
            padding-bottom: 24px;
          }
        }
      }

      .reorder-button {
        background-color: var(--fg-cta, var(--fg-fallback-cta));
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        height: fit-content;
        padding: 11.5px 24px 11.5px 24px;
        border-radius: 48px;
        font-size: var(--fg-font-size--xl);
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
        font-weight: 600;
      }

      .reorder-button:disabled {
        cursor: wait;
        opacity: 0.5;
      }

      .reorder-button fg-loader {
        margin: -8px 0;
      }

      .reorder-button-label {
        margin-left: 4px;
      }

      .order-title {
        font-weight: 600;
        font-size: var(--fg-font-size--3xl);
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
        line-height: 140%;
      }

      .order-subtitle {
        font-weight: 400;
        margin-top: 4px;
        margin-bottom: 16px;
        font-family: var(
          --fg-font-family--serif,
          var(--fg-fallback-font-family--serif)
        );
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
        line-height: 160%;
      }
      .shipping-and-totals-container {
        border-top: #d6dae1 solid 1px;
        padding-top: 16px;
        display: flex;
        justify-content: space-between;

        @media screen and (max-width: ${BREAKPOINT_LARGE_PX}px) {
          && {
            flex-direction: column;
          }
        }
      }

      .item-title {
        font-weight: 600;
        font-size: var(--fg-font-size--xl);
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
        line-height: 140%;
        width: fit-content;
      }

      .item-text {
        margin-top: 4px;
        font-size: 16px;
        line-height: 160%;
        font-weight: 400;
        display: flex;
        align-items: center;
        width: fit-content;
      }

      .items-container {
        display: flex;
        flex-direction: column;
        flex: 1;

        @media screen and (max-width: ${BREAKPOINT_LARGE_PX}px) {
          && {
            flex: none;
          }
        }
        @media screen and (max-width: ${BREAKPOINT_SMALL_PX}px) {
          && {
            flex: 1;
            margin-top: 16px;
          }
        }
      }

      .total-item-container {
        display: flex;
        justify-content: space-between;

        span {
          text-transform: capitalize;
        }
      }

      .total-excl-vat {
        font-weight: 600;
        line-height: 100%;
      }

      .total-excl-vat-value {
        font-size: var(--fg-font-size--3xl);
      }
    `,
  ];
}
